/**
 * Create.jsx — bot creation.
 *
 * THE REASON THIS IS A MINI APP RATHER THAN A BOT.
 *
 * Fifteen parameters in a chat is fifteen sequential prompts, each one a
 * place to abandon. Everything else in this app is arguably better served by
 * the Telegram bot that already exists; this screen is not.
 *
 * Segmented controls rather than dropdowns throughout: a three-option choice
 * is one tap on a visible segment versus three taps plus a scroll on a native
 * select. That difference is most of the argument for a webview.
 */

import React, { useEffect, useState } from "react";
import * as api from "../lib/api";
import * as tg from "../lib/telegram";
import { C, money, Label, Panel, Button, Segment, Empty } from "../theme";

const SIZING = [
  { value: "fixed",        label: "FIXED" },
  { value: "proportional", label: "PROP." },
  { value: "kelly",        label: "KELLY" },
];

export default function Create({ onDone, onPickTrader, trader }) {
  const [limits, setLimits] = useState(null);
  const [mode, setMode] = useState("paper");
  const [sizing, setSizing] = useState("fixed");
  const [amount, setAmount] = useState(25);
  const [trailStop, setTrailStop] = useState(8);
  const [maxDrawdown, setMaxDrawdown] = useState(150);
  const [minOdds, setMinOdds] = useState(0.15);
  const [maxOdds, setMaxOdds] = useState(0.85);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.planLimits().then(setLimits).catch(() => setLimits(null));
  }, []);

  // Plan limits are ALSO enforced server-side on /api/bot/start. This is a
  // display of the constraint, never the constraint itself — the mini app
  // must not become a route around tier caps.
  const liveUsed  = limits?.live_bots_used ?? limits?.live_bots ?? null;
  const liveMax   = limits?.live_bots_limit ?? limits?.max_live_bots ?? null;
  const liveFull  = liveUsed != null && liveMax != null && liveUsed >= liveMax;

  useEffect(() => {
    // If live is unavailable, do not leave a selection the server will reject.
    if (liveFull && mode === "live") setMode("paper");
  }, [liveFull, mode]);

  const submit = async () => {
    if (!trader) { setErr("Pick a trader first."); return; }
    setBusy(true); setErr(null);
    try {
      await api.createBot({
        target_trader: trader.address || trader.eoa_address || trader.wallet,
        trader_name: trader.username || trader.name,
        mode,
        sizing_mode: sizing,
        fixed_amount: Number(amount),
        trailing_stop_pct: Number(trailStop),
        max_drawdown: Number(maxDrawdown),
        min_odds: Number(minOdds),
        max_odds: Number(maxOdds),
      });
      tg.haptic.ok();
      onDone();
    } catch (e) {
      tg.haptic.err();
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: 14 }}>

      <Label>TRADER</Label>
      <Panel onClick={onPickTrader} style={{ marginBottom: 16 }}>
        {trader ? (
          <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "center", gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, color: C.ink }}>
                {trader.username || trader.name || "Trader"}
              </div>
              <div style={{ fontSize: 11, color: C.dim, marginTop: 3 }}>
                {money(trader.total_pnl)} · {Number(trader.win_rate || 0).toFixed(0)}% win
              </div>
            </div>
            <span style={{ color: C.dim, fontSize: 16, flexShrink: 0 }}>›</span>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "center" }}>
            <span style={{ fontSize: 13, color: C.dim }}>Choose a trader to copy</span>
            <span style={{ color: C.amber, fontSize: 16 }}>›</span>
          </div>
        )}
      </Panel>

      <Label>MODE</Label>
      <Segment
        value={mode}
        onChange={setMode}
        style={{ marginBottom: 6 }}
        options={[
          { value: "paper", label: "PAPER" },
          { value: "live",  label: liveFull ? "LIVE — FULL" : "LIVE",
            color: liveFull ? C.dim : C.green },
        ]}
      />
      {liveFull && (
        <p style={{ fontSize: 10.5, color: C.red, margin: "0 0 16px",
                    lineHeight: 1.6 }}>
          Live bot limit reached ({liveUsed} of {liveMax}). This bot will run in
          paper mode — upgrade at polyedge.online for more live bots.
        </p>
      )}
      {!liveFull && <div style={{ height: 16 }} />}

      <Label>POSITION SIZING</Label>
      <Segment options={SIZING} value={sizing} onChange={setSizing}
               style={{ marginBottom: 9 }} />
      <NumRow label="Amount per trade" value={amount} onChange={setAmount}
              prefix="$" step={5} min={1} />

      <Label style={{ marginTop: 16 }}>RISK LIMITS</Label>
      <div style={{ border: `0.5px solid ${C.border}`, background: C.bg1,
                    marginBottom: 16 }}>
        <NumRow flat label="Trailing stop" value={trailStop}
                onChange={setTrailStop} suffix="%" step={1} min={0} max={100} />
        <NumRow flat label="Max drawdown" value={maxDrawdown}
                onChange={setMaxDrawdown} prefix="$" step={25} min={0} />
        <NumRow flat last label="Min odds" value={minOdds}
                onChange={setMinOdds} step={0.05} min={0.01} max={0.99} dp={2} />
        <NumRow flat last label="Max odds" value={maxOdds}
                onChange={setMaxOdds} step={0.05} min={0.01} max={0.99} dp={2} />
      </div>

      {err && <p style={{ fontSize: 11.5, color: C.red, margin: "0 0 12px" }}>{err}</p>}

      <Button onClick={submit} disabled={busy || !trader}>
        {busy ? "Creating…" : `Create ${mode} bot`}
      </Button>

      <p style={{ fontSize: 10.5, color: C.dim, lineHeight: 1.7,
                  marginTop: 14, textAlign: "center" }}>
        Bots run on PolyEdge servers and keep running when you close Telegram.
      </p>
    </div>
  );
}

/**
 * Stepper rather than a text input. A number keyboard in a Telegram webview
 * covers most of the screen and is fiddly to dismiss; +/- avoids it entirely
 * for values that move in predictable increments.
 */
function NumRow({ label, value, onChange, prefix = "", suffix = "",
                 step = 1, min, max, dp = 0, flat, last }) {
  const clamp = (v) => {
    let n = Number(v.toFixed(dp));
    if (min != null) n = Math.max(min, n);
    if (max != null) n = Math.min(max, n);
    return n;
  };
  const btn = {
    width: 34, height: 30, display: "flex", alignItems: "center",
    justifyContent: "center", border: `0.5px solid ${C.border}`,
    color: C.sub, fontSize: 15, cursor: "pointer", userSelect: "none",
    touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
  };
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: flat ? "9px 12px" : "11px 12px", gap: 10,
      ...(flat
        ? { borderBottom: last ? "none" : `0.5px solid rgba(245,166,35,0.12)` }
        : { border: `0.5px solid ${C.border}`, background: C.bg1 }),
    }}>
      <span style={{ fontSize: 12, color: C.dim }}>{label}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={btn} onClick={() => onChange(clamp(value - step))}>−</div>
        <span style={{ fontSize: 14, color: C.ink, minWidth: 62,
                       textAlign: "center" }}>
          {prefix}{Number(value).toFixed(dp)}{suffix}
        </span>
        <div style={btn} onClick={() => onChange(clamp(value + step))}>+</div>
      </div>
    </div>
  );
}
